import * as turf from "@turf/turf"

type Point = [number, number]

interface ElevationProfileProps {
  elevations: number[]
  points: Point[]
}

export const ElevationProfile = ({ elevations, points }: ElevationProfileProps) => {
  if (elevations.length === 0) return null

  const w = 400,
    h = 160
  const paddingTop = 18
  const paddingBottom = 28
  const paddingLeft = 48
  const paddingRight = 16
  const svgWidth = w + paddingLeft + paddingRight
  const svgHeight = h + paddingTop + paddingBottom
  const min = Math.min(...elevations)
  const max = Math.max(...elevations)
  const mid = (min + max) / 2
  const scaleY = (e: number) => paddingTop + h - ((e - min) / (max - min + 1e-6)) * h
  const scaleX = (i: number) => paddingLeft + (i / (elevations.length - 1)) * w
  const path = elevations.map((e, i) => `${i === 0 ? "M" : "L"}${scaleX(i)},${scaleY(e)}`).join(" ")
  const areaPath = path + ` L${scaleX(elevations.length - 1)},${paddingTop + h} L${scaleX(0)},${paddingTop + h} Z`

  const primaryColor = "#3b82f6"

  // Compute stats
  let positiveDiff = 0
  let negativeDiff = 0
  let maxSlope = 0
  let totalDist = 0
  if (elevations.length > 1 && points.length === 2) {
    const line = turf.lineString(points)
    const lineLength = turf.length(line, { units: "meters" })
    totalDist = lineLength

    for (let i = 1; i < elevations.length; i++) {
      const dz = elevations[i] - elevations[i - 1]
      if (dz > 0) positiveDiff += dz
      if (dz < 0) negativeDiff += dz
      const dx = lineLength / (elevations.length - 1)
      const slope = dx > 0 ? (Math.abs(dz) / dx) * 100 : 0
      if (slope > maxSlope) maxSlope = slope
    }
  }

  // Elevation label indices
  const numLabels = 10
  const step = elevations.length > 1 ? (elevations.length - 1) / (numLabels - 1) : 1
  const labelIndices = Array.from({ length: numLabels }, (_, i) => Math.round(i * step))

  return (
    <>
      <svg width={svgWidth} height={svgHeight}>
        <defs>
          <linearGradient id="altimetry-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={primaryColor} stopOpacity={0.3} />
            <stop offset="100%" stopColor={primaryColor} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        {/* Grid lines */}
        {[max, mid, min].map((val, i) => (
          <g key={i}>
            <line
              x1={paddingLeft}
              y1={scaleY(val)}
              x2={paddingLeft + w}
              y2={scaleY(val)}
              stroke="#E6E6E6"
              strokeWidth={1}
            />
            <text
              x={paddingLeft - 6}
              y={scaleY(val) + 3}
              fontSize={10}
              textAnchor="end"
              fill="#838383"
              fontFamily="sans-serif"
            >
              {val.toFixed(0)}m
            </text>
          </g>
        ))}
        {/* Area fill */}
        <path d={areaPath} fill="url(#altimetry-gradient)" />
        {/* Line */}
        <path d={path} fill="none" stroke={primaryColor} strokeWidth={2} />
        {/* Elevation labels */}
        {labelIndices.map((idx, i) => (
          <g key={i}>
            <circle cx={scaleX(idx)} cy={scaleY(elevations[idx])} r={2} fill={primaryColor} />
            <text x={scaleX(idx)} y={scaleY(elevations[idx]) - 6} fontSize={9} textAnchor="middle" fill="#333">
              {elevations[idx].toFixed(1)}m
            </text>
          </g>
        ))}
        {/* X-axis labels */}
        <text
          x={paddingLeft}
          y={paddingTop + h + 18}
          fontSize={10}
          textAnchor="start"
          fill="#838383"
          fontFamily="sans-serif"
        >
          0
        </text>
        <text
          x={paddingLeft + w}
          y={paddingTop + h + 18}
          fontSize={10}
          textAnchor="end"
          fill="#838383"
          fontFamily="sans-serif"
        >
          {(totalDist / 1000).toFixed(2)} km
        </text>
      </svg>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-3 pt-3 border-t border-gray-200">
        <div>
          <div className="text-xs text-gray-500">Dénivelé positif</div>
          <div className="text-sm font-semibold text-gray-800">{positiveDiff.toFixed(1)} m</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Dénivelé négatif</div>
          <div className="text-sm font-semibold text-gray-800">{Math.abs(negativeDiff).toFixed(1)} m</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Distance totale</div>
          <div className="text-sm font-semibold text-gray-800">{(totalDist / 1000).toFixed(2)} km</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Pente maximale</div>
          <div className="text-sm font-semibold text-gray-800">{maxSlope.toFixed(1)} %</div>
        </div>
      </div>
    </>
  )
}
